import { FC, Fragment } from 'react'
import { TrashIcon } from 'lucide-react'
import { MoneyView } from '../../../common/components/MoneyView'
import { ChatPreviewComment } from '../../preview/ChatPreviewComment'
import { PreviewAutocomplete } from '../../../features/preview-autocomplete'
import { ApiCatalogSearchItem } from '../../../api/catalog/types'
import styled from '@emotion/styled'
import { ChatPreviewIconButton } from '../../preview/ChatPreviewIconButton'
import { ChatPreviewEditableDescription } from '../../preview/ChatPreviewEditableDescription'
import { PreviewItem } from '../../../features/preview-edits/model/types'
import { PreviewSkeleton } from '../../../features/preview-edits/ui/PreviewSkeleton'
import { PreviewUnitSelect } from '../../../features/preview-edits/ui/PreviewUnitSelect'
import { PreviewQuantityInput } from '../../../features/preview-edits/ui/PreviewQuantityInput'
import { PreviewEditableValue } from '../../../features/preview-edits/ui/PreviewEditableValue'
import { PreviewDateInput } from '../../../features/preview-edits/ui/PreviewDateInput'

const LeftCell = styled('td')({
  '&&': {
    textAlign: 'left',
  },
})

const ActionsCell = styled('td')({
  whiteSpace: 'nowrap',
})

const ContentOffset = styled('div')(({ theme }) => ({
  paddingInline: theme.spacing.xs + 1,
}))

const OriginalRequest = styled(ContentOffset)`
  opacity: 0.5;
`

interface ChatDesktopPreviewItemProps {
  item: PreviewItem
  editing: boolean
  onEditToggle: () => void
  onSelect: (selection: ApiCatalogSearchItem) => void
  onRemove: () => void
  onStartEditingQuantity: () => void
  onStopEditingQuantity: () => void
  onUnitChange: (unitIndex: number) => void
  onQuantityChange: (quantity: number) => void
  isAgent: boolean
  onDateChange: (date: string | undefined) => void
}

export const ChatDesktopPreviewItem: FC<ChatDesktopPreviewItemProps> = ({ item, editing, onEditToggle, onSelect, onRemove, onStartEditingQuantity, onStopEditingQuantity, onUnitChange, onQuantityChange, isAgent, onDateChange }) => {
  if (item.type === 'original') {
    const apiItem = item.previewItem
    const editingQuantity = !!item.changedUnitsOfMeasure && !editing

    return (
      <Fragment>
        <tr
          onBlur={
            editingQuantity
              ? (e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    onStopEditingQuantity()
                  }
                }
              : undefined
          }
        >
          <td>{apiItem.rownum}</td>

          {apiItem.quotedItem && !editing ? (
            <>
              <LeftCell>
                <ChatPreviewEditableDescription onClick={onEditToggle}>
                  {apiItem.quotedItem.description}
                </ChatPreviewEditableDescription>
              </LeftCell>
              {item.changedUnitsOfMeasure ? (
                <>
                  <td>
                    <PreviewQuantityInput
                      value={item.changedUnitsOfMeasure.value}
                      integer={item.changedUnitsOfMeasure.type === 'units'}
                      onChange={onQuantityChange}
                      autoFocus
                    />
                  </td>
                  <td>
                    <PreviewUnitSelect
                      units={apiItem.quotedItem.skuUnitsOfMeasure}
                      selectedIndex={apiItem.quotedItem.skuUnitsOfMeasure.indexOf(item.changedUnitsOfMeasure.type)}
                      onChange={onUnitChange}
                    />
                  </td>
                  <td><PreviewSkeleton /></td>
                  <td><PreviewSkeleton /></td>
                  <td><PreviewSkeleton /></td>
                  <td>
                    {isAgent ? (
                      <PreviewDateInput value={item.changedDate ?? apiItem.quotedItem.date} onChange={onDateChange} />
                    ) : (
                      <PreviewSkeleton />
                    )}
                  </td>
                </>
              ) : (
                <>
                  <td><PreviewEditableValue onClick={onStartEditingQuantity}>{apiItem.quotedItem.units}</PreviewEditableValue></td>
                  <td><PreviewEditableValue onClick={onStartEditingQuantity}>{apiItem.quotedItem.quantity}</PreviewEditableValue></td>
                  <td><MoneyView value={apiItem.quotedItem.price} /></td>
                  <td>{apiItem.quotedItem.discount}</td>
                  <td><MoneyView value={apiItem.quotedItem.subtotal} /></td>
                  <td>
                    {isAgent ? (
                      <PreviewDateInput value={item.changedDate ?? apiItem.quotedItem.date} onChange={onDateChange} />
                    ) : (
                      apiItem.quotedItem.date
                    )}
                  </td>
                </>
              )}
            </>
          ) : (
            <LeftCell colSpan={7}>
              <PreviewAutocomplete
                autoFocus={editing}
                defaultTokens={apiItem.quotedItem?.tokens}
                originalRequest={apiItem.originalRequest}
                searchRequest={apiItem.searchRequest}
                onCancel={editing ? onEditToggle : undefined}
                onSelect={onSelect}
              />
            </LeftCell>
          )}

          <ActionsCell>
            <ChatPreviewIconButton onClick={onRemove}>
              <TrashIcon size={16} />
            </ChatPreviewIconButton>
          </ActionsCell>
        </tr>

        {apiItem.quotedItem?.comment && !editing && (
          <tr>
            <td></td>
            <LeftCell colSpan={7}>
              <ContentOffset>
                <ChatPreviewComment>{apiItem.quotedItem.comment}</ChatPreviewComment>
              </ContentOffset>
            </LeftCell>
          </tr>
        )}

        <tr>
          <td></td>
          <LeftCell colSpan={7}>
            <OriginalRequest>{apiItem.originalRequest}</OriginalRequest>
          </LeftCell>
        </tr>
      </Fragment>
    )
  }

  const rownum = item.type === 'edited' ? item.previewItem.rownum : undefined
  const selectedUnitIndex = item.catalogItem.unitsOfMeasure.indexOf(item.unitsOfMeasure.type)

  return (
    <Fragment>
      <tr>
        <td>{rownum}</td>

        {editing ? (
          <LeftCell colSpan={7}>
            <PreviewAutocomplete
              autoFocus
              defaultTokens={item.catalogItem.tokens}
              originalRequest={item.type === 'edited' ? item.previewItem.originalRequest : undefined}
              searchRequest={item.type === 'edited' ? item.previewItem.searchRequest : undefined}
              onCancel={onEditToggle}
              onSelect={onSelect}
            />
          </LeftCell>
        ) : (
          <>
            <LeftCell>
              <ChatPreviewEditableDescription onClick={onEditToggle}>
                {item.catalogItem.description}
              </ChatPreviewEditableDescription>
            </LeftCell>
            <td>
              <PreviewQuantityInput value={item.unitsOfMeasure.value} integer={item.unitsOfMeasure.type === 'units'} onChange={onQuantityChange} />
            </td>
            <td>
              <PreviewUnitSelect units={item.catalogItem.unitsOfMeasure} selectedIndex={selectedUnitIndex} onChange={onUnitChange} />
            </td>
            <td><PreviewSkeleton /></td>
            <td><PreviewSkeleton /></td>
            <td><PreviewSkeleton /></td>
            <td>
              {isAgent ? (
                <PreviewDateInput value={item.date} onChange={onDateChange} />
              ) : (
                <PreviewSkeleton />
              )}
            </td>
          </>
        )}
        <ActionsCell>
          <ChatPreviewIconButton onClick={onRemove}>
            <TrashIcon size={16} />
          </ChatPreviewIconButton>
        </ActionsCell>
      </tr>

      {item.type === 'edited' && (
        <tr>
          <td></td>
          <LeftCell colSpan={7}>
            <OriginalRequest>{item.previewItem.originalRequest}</OriginalRequest>
          </LeftCell>
        </tr>
      )}
    </Fragment>
  )
}
